import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, Chip, CircularProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LagbasLogo from '../../../../components/LagbasLogo';
import HeroBg from '../components/HeroBg';
import { db } from '../../../../firebase/config';
import {
  collection, doc, getDoc, query, orderBy, onSnapshot,
} from 'firebase/firestore';
import { MAPPN_ACCENT, TECH_TYPES } from '../constants';

export default function MappNTech() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub;
    async function init() {
      setLoading(true);
      const cSnap = await getDoc(doc(db, 'mappnCases', caseId));
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      setCaseData({ id: cSnap.id, ...cSnap.data() });
      unsub = onSnapshot(
        query(collection(db, 'mappnCases', caseId, 'components'), orderBy('createdAt', 'desc')),
        snap => {
          setComponents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        }
      );
    }
    init();
    return () => unsub?.();
  }, [caseId, navigate]);

  const grouped = useMemo(() => {
    const map = new Map();

    for (const t of (caseData?.techStack || [])) {
      if (!t.name) continue;
      const key = `${t.name.toLowerCase()}__${t.type}`;
      if (!map.has(key)) map.set(key, { name: t.name, type: t.type, version: t.version || '', url: t.url || '', sources: [] });
      map.get(key).sources.push({ id: 'case', title: 'Case (alla)' });
    }

    for (const comp of components) {
      for (const t of (comp.techStack || [])) {
        if (!t.name) continue;
        const key = `${t.name.toLowerCase()}__${t.type}`;
        if (!map.has(key)) map.set(key, { name: t.name, type: t.type, version: t.version || '', url: t.url || '', sources: [] });
        const entry = map.get(key);
        if (!entry.sources.find(s => s.id === comp.id)) {
          entry.sources.push({ id: comp.id, title: comp.title || '(Namnlös)' });
        }
        if (!entry.version && t.version) entry.version = t.version;
        if (!entry.url && t.url) entry.url = t.url;
      }
    }

    const result = {};
    for (const entry of map.values()) {
      if (!result[entry.type]) result[entry.type] = [];
      result[entry.type].push(entry);
    }
    for (const key of Object.keys(result)) {
      result[key].sort((a, b) => b.sources.length - a.sources.length);
    }
    return result;
  }, [caseData, components]);

  const TABS = [
    { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
    { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
  ];

  if (loading && !caseData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: MAPPN_ACCENT }} />
      </Box>
    );
  }

  const totalTech = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);

  const hasHero = !!(caseData?.heroVideoUrl || caseData?.heroImageUrl);

  return (
    <>
    <HeroBg caseData={caseData} />
    <Box sx={{ minHeight: '100vh', bgcolor: hasHero ? 'transparent' : '#0d1117', color: 'white', position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <Box sx={{
        bgcolor:        hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
        backdropFilter: hasHero ? 'blur(22px) saturate(1.3)' : 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {caseData?.heroImageUrl && (
          <Box component="img" src={caseData.heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{caseData?.name || '…'}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {totalTech} unika teknologier · {components.length} komponenter
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {TABS.map(tab => (
            <Button
              key={tab.path}
              startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color: tab.path === 'tech' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'tech' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1,
                '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: MAPPN_ACCENT }} />
          </Box>
        ) : totalTech === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <HubIcon sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.1)', mb: 2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mb: 0.5 }}>Ingen tech stack registrerad ännu</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.15)' }}>
              Lägg till teknologier i komponenter eller case-inställningar
            </Typography>
          </Box>
        ) : (
          TECH_TYPES.map(tt => {
            const entries = grouped[tt.key] || [];
            if (!entries.length) return null;
            return (
              <Box key={tt.key} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{
                    px: 1, py: 0.25, borderRadius: 1,
                    bgcolor: tt.bg + '28', border: `1px solid ${tt.bg}50`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.68rem', fontWeight: 700,
                      color: tt.key === 'other' ? '#BDBDBD' : tt.bg,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                      {tt.label}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
                    {entries.length}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {entries.map(entry => (
                    <Box key={`${entry.name}__${entry.type}`} sx={{
                      bgcolor: '#161b22',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderTop: `2px solid ${tt.bg}`,
                      borderRadius: 1.5,
                      p: 1.5,
                      minWidth: 170,
                      maxWidth: 260,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'white', flex: 1, lineHeight: 1.25 }}>
                          {entry.name}
                        </Typography>
                        {entry.url && (
                          <IconButton
                            size="small"
                            href={entry.url} target="_blank" rel="noopener noreferrer"
                            sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: MAPPN_ACCENT }, p: 0.3, flexShrink: 0 }}
                          >
                            <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                          </IconButton>
                        )}
                      </Box>
                      {entry.version && (
                        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.28)', mb: 0.75 }}>
                          v{entry.version}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: entry.version ? 0 : 0.75 }}>
                        {entry.sources.map(src => (
                          <Chip
                            key={src.id}
                            label={src.title}
                            size="small"
                            sx={{
                              height: 18, fontSize: '0.6rem',
                              bgcolor: src.id === 'case' ? MAPPN_ACCENT + '22' : 'rgba(255,255,255,0.06)',
                              color: src.id === 'case' ? MAPPN_ACCENT : 'rgba(255,255,255,0.45)',
                              border: `1px solid ${src.id === 'case' ? MAPPN_ACCENT + '44' : 'rgba(255,255,255,0.06)'}`,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
    </>
  );
}
